import { MiraSavedComparison, MiraSavedComparisonKind } from "@/data/models/MiraSavedComparison";
import {
  MIRA_COMPARISON_BRANCHES,
  MIRA_COMPARISON_INSURERS,
  MIRA_COMPARISON_MEMBERS,
  MIRA_COMPARISON_NONE,
  MiraComparisonMode,
} from "@/utils/MiraComparisonView";
import User from "@/data/models/User";
import { MIRA_COMPARISON_NAME_LIMIT } from "@/utils/MiraStackedTrendView";

/** At most this many saved comparisons per user and client (Taimoor, 2026-11-02). */
export const MIRA_SAVED_COMPARISONS_MAX = 5;

/**
 * At most this many entities in one saved comparison: the timeline names up to this many bars, above it
 * it draws the five levels and the picked order would be lost.
 */
export const MIRA_SAVED_COMPARISON_ENTITIES_MAX = MIRA_COMPARISON_NAME_LIMIT;

/** The prefix of a saved comparison's option in the downwards picker, beside "NONE" and the kind. */
const SAVED_OPTION_PREFIX = "saved:";

/** The picker option that chooses a saved comparison. */
export function savedComparisonOption(id: string): string {
  return `${SAVED_OPTION_PREFIX}${id}`;
}

/** The saved comparison an option chooses, or `null` for "NONE" and the kinds. */
export function savedComparisonIdOf(option: string): string | null {
  return option.startsWith(SAVED_OPTION_PREFIX) ? option.slice(SAVED_OPTION_PREFIX.length) : null;
}

/** The user's saved comparisons of the given kinds in one client, in the order they were made. */
export function savedComparisonsFor(
  user: Pick<User, "miraComparisons"> | null | undefined,
  clientId: string | null | undefined,
  kinds: MiraSavedComparisonKind[]
): MiraSavedComparison[] {
  if (!clientId || kinds.length === 0) {
    return [];
  }
  return (user?.miraComparisons?.[clientId] ?? []).filter((comparison) => kinds.includes(comparison.kind));
}

/** The list with the comparison replaced where its id is already there, else added at the end. */
export function upsertSavedComparison(list: MiraSavedComparison[], comparison: MiraSavedComparison): MiraSavedComparison[] {
  return list.some((entry) => entry.id === comparison.id)
    ? list.map((entry) => (entry.id === comparison.id ? comparison : entry))
    : [...list, comparison];
}

export function removeSavedComparison(list: MiraSavedComparison[], id: string): MiraSavedComparison[] {
  return list.filter((entry) => entry.id !== id);
}

/** The items whose id was picked, in the picked order; a picked id with no item (a deleted branch) is skipped. */
export function inPickedOrder<T extends { id: string }>(items: T[], pickedIds: string[]): T[] {
  return pickedIds.map((id) => items.find((item) => item.id === id)).filter((item): item is T => item !== undefined);
}

const SAVED_KINDS: readonly MiraComparisonMode[] = [MIRA_COMPARISON_BRANCHES, MIRA_COMPARISON_MEMBERS, MIRA_COMPARISON_INSURERS];

/** The kinds a client's downwards options compare (Standorte or Mandanten, Versicherer), in their order. */
export function savedComparisonKindsOf(downwardOptions: MiraComparisonMode[]): MiraSavedComparisonKind[] {
  // The constants are typed as the whole union, so the filter does not narrow on its own.
  return downwardOptions.filter((option) => SAVED_KINDS.includes(option)) as MiraSavedComparisonKind[];
}

/** The downwards picker: "Kein Vergleich", all of the kind (ranked), then the saved comparisons by name. */
export function downwardMenuOptionsOf(downwardOptions: MiraComparisonMode[], saved: MiraSavedComparison[]): string[] {
  return [...downwardOptions, ...saved.map((comparison) => savedComparisonOption(comparison.id))];
}

/**
 * The saved comparison in effect: chosen, still saved, and of the kind chosen downwards. A deleted one, or
 * one of another client, leaves all of the kind.
 */
export function activeSavedComparisonOf(
  savedId: string | null,
  downwardMode: MiraComparisonMode,
  saved: MiraSavedComparison[]
): MiraSavedComparison | null {
  if (!savedId || downwardMode === MIRA_COMPARISON_NONE) {
    return null;
  }
  return saved.find((comparison) => comparison.id === savedId && comparison.kind === downwardMode) ?? null;
}

/** What choosing a downwards option stores: the kind (the saved comparison's own), and its id where it is one. */
export function downwardChoiceOf(
  option: string,
  saved: MiraSavedComparison[]
): { mode: MiraComparisonMode; savedId: string | null } {
  const savedId = savedComparisonIdOf(option);
  if (!savedId) {
    return { mode: option as MiraComparisonMode, savedId: null };
  }
  const comparison = saved.find((entry) => entry.id === savedId);
  return { mode: comparison?.kind ?? MIRA_COMPARISON_NONE, savedId: comparison ? savedId : null };
}
